/***************************************************************************
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { isEmpty, isFinite, isNumber, isString } from 'lodash-es';
import { A3dElement } from '../core';
import { customElement, property } from 'lit/decorators.js';
var A3dEnvironmentErrorNames;
(function (A3dEnvironmentErrorNames) {
    A3dEnvironmentErrorNames["NO_SRC_FILE_PROVIDED"] = "A3dEnvironmentNoSrcFileProvided";
    A3dEnvironmentErrorNames["BAD_BLUR_LEVEL"] = "A3dEnvironmentBadBlurLevel";
    A3dEnvironmentErrorNames["BAD_ROTATION_Y"] = "A3dEnvironmentBadRotationY";
    A3dEnvironmentErrorNames["BAD_INTENSITY"] = "A3dEnvironmentBadIntensity";
})(A3dEnvironmentErrorNames || (A3dEnvironmentErrorNames = {}));
const A3dEnvironmentErrorMessages = {
    [A3dEnvironmentErrorNames.NO_SRC_FILE_PROVIDED]: 'Attribute src is required',
    [A3dEnvironmentErrorNames.BAD_BLUR_LEVEL]: 'Attribute blur-level have to be a number between 0 and 1',
    [A3dEnvironmentErrorNames.BAD_ROTATION_Y]: 'Attribute rotation-y have to be a number',
    [A3dEnvironmentErrorNames.BAD_INTENSITY]: 'Attribute intensity have to be a number',
};
let A3dEnv = class A3dEnv extends A3dElement {
    constructor() {
        super(...arguments);
        /**
         * Source (url) of the environment file.
         * @attr {string} src - url
         */
        this.src = '';
        /**
         * Sets the environment visible in the background.
         * @attr {boolean} visible - true or false
         */
        this.visible = false;
        /**
         * Blur level of the environment from 0 (not blurred) to 1.
         * @attr {number} blur-level - 0 to 1
         */
        this.blurLevel = 0;
        /**
         * Rotation Y of the environment in rad.
         * @attr {number} rotation-y - rad
         */
        this.rotationY = 0;
        /**
         * Intensity of the environment.
         * @attr {number} intensity - 0 to 1
         */
        this.intensity = 1;
    }
    /**
     * @internal
     */
    async _initElement() {
        if (!this._viewer) {
            return;
        }
        if (this.visible) {
            await this._viewer.loadEnvironment(this.src, {
                lighting: true,
                skybox: true,
            });
        }
        this._viewer.environmentConfig = {
            blur: this.blurLevel,
            rotation: this.rotationY,
            intensity: this.intensity,
        };
    }
    update(_changedProperties) {
        if (!this._validateProperties())
            return;
        super.update(_changedProperties);
        if (!this._viewer) {
            return;
        }
        if (_changedProperties.has('src')) {
            this._viewer.loadEnvironment(this.src);
        }
        if (_changedProperties.has('visible')) {
            this.visible
                ? this._viewer.loadEnvironment('auto', { skybox: true })
                : this._viewer.resetEnvironment({ skybox: true });
        }
        if (_changedProperties.has('blurLevel')) {
            this._viewer.environmentConfig = { blur: this.blurLevel };
        }
        if (_changedProperties.has('intensity')) {
            this._viewer.environmentConfig = { intensity: this.intensity };
        }
        if (_changedProperties.has('rotationY')) {
            this._viewer.environmentConfig = { rotation: this.rotationY };
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (!this._viewer) {
            return;
        }
        this._viewer.resetEnvironment();
    }
    /**
     * @internal
     */
    exposedData() {
        return {
            src: this.src,
            visible: this.visible,
            rotationY: (this.rotationY + Math.PI) % (2 * Math.PI),
            blurLevel: this.blurLevel,
            intensity: this.intensity,
        };
    }
    /**
     * @internal
     */
    _validateSrc() {
        const isValid = isString(this.src) && !isEmpty(this.src);
        if (!isValid) {
            this._dispatchErrorEvent(A3dEnvironmentErrorNames.NO_SRC_FILE_PROVIDED, A3dEnvironmentErrorMessages[A3dEnvironmentErrorNames.NO_SRC_FILE_PROVIDED]);
        }
        return isValid;
    }
    /**
     * @internal
     */
    _validateBlurLevel() {
        const isValid = isNumber(this.blurLevel) && this.blurLevel >= 0 && this.blurLevel <= 1;
        if (!isValid) {
            this._dispatchErrorEvent(A3dEnvironmentErrorNames.BAD_BLUR_LEVEL, A3dEnvironmentErrorMessages[A3dEnvironmentErrorNames.BAD_BLUR_LEVEL]);
        }
        return isValid;
    }
    /**
     * @internal
     */
    _validateNumber(value, errorName) {
        const isValid = isFinite(value);
        if (!isValid) {
            this._dispatchErrorEvent(errorName, A3dEnvironmentErrorMessages[errorName]);
        }
        return isValid;
    }
    /**
     * @internal
     */
    _validateProperties() {
        let isValidBlurLevel = true;
        const isValidSrc = this._validateSrc();
        if (this.blurLevel) {
            isValidBlurLevel = this._validateBlurLevel();
        }
        const isValidRotationY = this._validateNumber(this.rotationY, A3dEnvironmentErrorNames.BAD_ROTATION_Y);
        const isValidIntensity = this._validateNumber(this.intensity, A3dEnvironmentErrorNames.BAD_INTENSITY);
        return (isValidSrc && isValidBlurLevel && isValidRotationY && isValidIntensity);
    }
};
__decorate([
    property({ type: String })
], A3dEnv.prototype, "src", void 0);
__decorate([
    property({ type: Boolean })
], A3dEnv.prototype, "visible", void 0);
__decorate([
    property({ type: Number, attribute: 'blur-level' })
], A3dEnv.prototype, "blurLevel", void 0);
__decorate([
    property({ type: Number, attribute: 'rotation-y' })
], A3dEnv.prototype, "rotationY", void 0);
__decorate([
    property({ type: Number })
], A3dEnv.prototype, "intensity", void 0);
A3dEnv = __decorate([
    customElement('a3d-environment')
], A3dEnv);
export { A3dEnv, A3dEnvironmentErrorMessages, A3dEnvironmentErrorNames };
